import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import { environment } from '@/environments/environment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FormGroup, Validators } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class HelperService {
  Version = 'v1.1';
  imgVersion = '20241214';
  DEVICE_INFO: { uuid: string, timezone: string, platform: string, resolution: string };
  timeoutGetUserItem = null;
  agentId = '';

  constructor(
    private router: Router,
    private dialogService: NbDialogService
  ) { }

  getAssetUrl(path: string): string {
    return `${environment.baseUrl}/assets/${path}`;
  }

  calcAvatarPos(index: number, zoom: number = 1): string {
    return "-" + (index % 10) * 96 * zoom + "px -" + Math.floor(index / 10) * 96 * zoom + "px";
  }

  convertDateStr(sDate: string, lang: string): string {
    if (lang == 'vi') {
      const date = new Date(sDate);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } else {
      return sDate;
    }
  }

  convertMonthStr(sMonth: string, lang: string): string {
    if (lang == 'vi') {
      const date = new Date(sMonth + '-01');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${year}`;
    } else {
      return sMonth;
    }
  }

  generateRandomPassword(length: number = 12, options?: {
    includeUppercase?: boolean,
    includeLowercase?: boolean,
    includeNumbers?: boolean,
    includeSymbols?: boolean
  }): string {
    const defaultOptions = {
      includeUppercase: true,
      includeLowercase: true,
      includeNumbers: true,
      includeSymbols: true
    };

    const finalOptions = { ...defaultOptions, ...options };

    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()-_=+[]{};:,.<>?';

    let characterPool = '';
    if (finalOptions.includeUppercase) characterPool += uppercase;
    if (finalOptions.includeLowercase) characterPool += lowercase;
    if (finalOptions.includeNumbers) characterPool += numbers;
    if (finalOptions.includeSymbols) characterPool += symbols;

    if (characterPool.length === 0) {
      throw new Error('至少启用一个字符类别');
    }

    let password = '';
    for (let i = 0; i < length; i++) {
      const index = Math.floor(Math.random() * characterPool.length);
      password += characterPool.charAt(index);
    }

    return password;
  }

  /** 根据 type 和 isFund 同步付款人字段的必填验证器 */
  syncPayerValidator(form: FormGroup): void {
    const payerControl = form.get('payer');
    const isRequired = form.value.type === 'expense' && form.value.isFund;
    if (isRequired) {
      payerControl?.setValidators([Validators.required]);
    } else {
      payerControl?.clearValidators();
    }
    payerControl?.updateValueAndValidity();
  }

  formatDate(date: Date, format: string = 'yyyy-MM-dd'): string {
    if (!date) return '';
    const map: { [key: string]: string } = {
      yyyy: date.getFullYear().toString(),
      yy: date.getFullYear().toString().slice(-2),
      MM: (date.getMonth() + 1).toString().padStart(2, '0'),
      dd: date.getDate().toString().padStart(2, '0'),
      HH: date.getHours().toString().padStart(2, '0'),
      mm: date.getMinutes().toString().padStart(2, '0'),
      ss: date.getSeconds().toString().padStart(2, '0')
    };

    return format.replace(/yyyy|yy|MM|dd|HH|mm|ss/g, matched => map[matched]);
  }

  exportExcel(data: any[], fileName: string, sheetName: string = 'data') {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = { Sheets: { [sheetName]: worksheet, }, SheetNames: [sheetName] };
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/octet-stream',
    });
    saveAs(blob, `${fileName}.xlsx`);
  }


  /**
   * 将JSON对象转换为Markdown格式
   * @param obj - 要转换的JSON对象
   * @param level - 当前标题级别（用于递归）
   * @param parentKey - 父级键名（用于生成标题）
   * @returns Markdown格式的字符串
   */
  jsonToMarkdown(data: any, depth: number = 0): string {
    const indent = '  '.repeat(depth); // 每层缩进2个空格

    // 处理null和undefined
    if (data === null) return 'null';
    if (data === undefined) return 'undefined';

    // 处理基本类型
    if (typeof data !== 'object') {
      return String(data);
    }

    // 处理数组
    if (Array.isArray(data)) {
      if (data.length === 0) return '[]';

      let result = '\n';
      data.forEach((item, index) => {
        if (index == 0) {
          result += `\n----\n`;
        }
        const childContent = this.jsonToMarkdown(item, depth);
        result += childContent;
        result += `\n----\n`;
      });
      return result;
    }

    // 处理对象
    const keys = Object.keys(data);
    if (keys.length === 0) return '{}';

    let result = '\n';
    keys.forEach(key => {
      const value = data[key];
      result += `${indent}- **${key}：** `;

      const childContent = this.jsonToMarkdown(value, depth);
      if (childContent.includes('\n')) {
        result += childContent.replace(/\n/g, `\n${indent}  `);
      } else {
        result += childContent;
      }
      result += '\n';
    });

    return result;
  }
}
